<script setup lang="ts">
import { computed, ref, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter, useRoute } from "vue-router";

import { toast } from "@/common/utils/toast";

import {
  methodFieldList,
  methodFieldShow,
  methodFieldCreate,
  methodFieldUpdate,
  methodFieldDelete,
} from "@/common/api/rest/method/method-field";

import type {
  MethodFieldType,
  MethodFieldPayloadType,
} from "@/common/types/method/method-field";

import { RegisterTheme } from "@/common/themes";
import { iconMenu, svgIcon } from "@/common/components";

import { methodFieldForm } from "@/components";

const { t, tm } = useI18n();
const router = useRouter();
const route = useRoute();

const fieldData = ref<MethodFieldType[]>([]);

const accountId = computed(() => String(route.params.id));
const groupId = computed(() => String(route.params.group));

const menuTabs = computed(() => [
  {
    key: "edit",
    icon: "home-line",
    label: t("common.labels.group"),
  },
  {
    key: "field",
    icon: "field",
    label: t("common.labels.fields"),
  },
  {
    key: "method",
    icon: "method",
    label: t("common.labels.methods"),
  },
  {
    key: "question",
    icon: "form",
    label: t("common.labels.questions"),
  },
]);

function back() {
  router.push(`/${accountId.value}/register/method`);
}

function goToTab(tab: string) {
  router.push(`/${accountId.value}/register/method/${groupId.value}/${tab}`);
}

function fetchFields() {
  methodFieldList(accountId.value, groupId.value, {
    page: 0,
    limit: 500,
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        fieldData.value = data.list;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

// FIELDS
function addField(field: MethodFieldPayloadType) {
  methodFieldCreate(accountId.value, groupId.value, field)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchFields();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function editField(field: MethodFieldType) {
  methodFieldUpdate(accountId.value, field.field_id, field)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchFields();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function delField(field: MethodFieldType) {
  methodFieldDelete(accountId.value, field.field_id)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchFields();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

onMounted(() => {
  fetchFields();
});
</script>

<template>
  <register-theme tab-active="method">
    <template #title>
      <div class="flex flex-row justify-between items-center w-full">
        <div class="flex flex-row gap-1 items-center">
          <svg-icon icon="method" size="2rem" />
          {{ t("common.labels.edit", { concat: t("common.labels.method") }) }}
        </div>

        <button type="button" class="btn btn-back btn-sm" @click="back()">
          <svg-icon icon="close" size="1.25rem" />
        </button>
      </div>
    </template>

    <template #content>
      <icon-menu :tabs="menuTabs" active="field" @selected="goToTab($event)">
        <template #content>
          <div class="title">
            {{ t("common.labels.fields") }}
          </div>

          <method-field-form
            :data="fieldData"
            @add="addField"
            @edit="editField"
            @del="delField"
          />
        </template>
      </icon-menu>
    </template>
  </register-theme>
</template>

<style lang="scss" scoped>
.title {
  display: flex;
  flex-direction: column;
  padding-inline: 0.5rem;
  border-bottom: 1px solid var(--border);
  margin-block: 2rem;
  padding-block-end: 0.5rem;
  font-size: 1.15rem;
  font-weight: bold;
  text-transform: uppercase;
}
</style>
